import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../components/context/AuthProvider";
import { InviteService } from "../../services/inviteService";
import type { InviteLinkPreview } from "../../types/invite";
import { Button, LoadingState, EmptyState, ErrorBoundary } from "../../components/ui";

const InvitePage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const auth = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState<InviteLinkPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      navigate(`/login?redirect=/invite/${code}`);
      return;
    }
    let active = true;
    InviteService.previewInviteLink(code as string)
      .then((p) => {
        if (active) setPreview(p);
      })
      .catch(() => {
        if (active) setError("This invite link could not be found.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [auth.isAuthenticated, code, navigate]);

  const handleAccept = async () => {
    try {
      const { campaignId } = await InviteService.acceptInviteLink(code as string);
      navigate(`/protected/campaigns/${campaignId}`);
    } catch {
      setError("Could not join this chronicle. The link may have expired.");
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="p-6 bg-zinc-950 text-white min-h-screen flex items-center justify-center">
      {children}
    </div>
  );

  if (loading) return shell(<LoadingState message="Loading invitation..." size="lg" />);

  if (error) {
    return shell(
      <EmptyState
        title="Invitation Error"
        description={error}
        icon="error"
        size="md"
        action={
          <Button onClick={() => navigate("/protected/campaigns")} variant="primary">
            Return to Chronicles
          </Button>
        }
      />
    );
  }

  if (preview && preview.status !== "valid") {
    const messages: Record<string, string> = {
      expired: "This invite link is no longer valid (it has expired).",
      revoked: "This invite link is no longer valid (it was revoked).",
      "already-member": `You are already part of ${preview.campaignName}.`,
    };
    return shell(
      <EmptyState
        title={preview.status === "already-member" ? "Already a member" : "Invite unavailable"}
        description={messages[preview.status]}
        icon={preview.status === "already-member" ? "info" : "error"}
        size="md"
        action={
          <Button
            onClick={() =>
              navigate(
                preview.status === "already-member"
                  ? `/protected/campaigns/${preview.campaignId}`
                  : "/protected/campaigns"
              )
            }
            variant="primary"
          >
            {preview.status === "already-member" ? "Go to Chronicle" : "Return to Chronicles"}
          </Button>
        }
      />
    );
  }

  return (
    <ErrorBoundary errorMessage="There was an error processing the invitation. Please try again later.">
      {shell(
        <div className="bg-zinc-900 border border-zinc-800 p-8 w-full max-w-lg text-center">
          <p className="text-amber-400/80 text-xs tracking-[0.45em] uppercase mb-3">
            You&apos;re invited
          </p>
          <h1 className="text-2xl font-light text-white tracking-wide mb-2">Chronicle Summons</h1>
          <p className="mb-6 text-zinc-300">
            You have been invited to join{" "}
            <span className="text-white font-medium">{preview?.campaignName}</span>.
          </p>
          <div className="flex justify-center gap-3">
            <Button onClick={handleAccept} variant="primary" size="md" data-testid="accept-link">
              Accept
            </Button>
            <Button
              onClick={() => navigate("/protected/campaigns")}
              variant="secondary"
              size="md"
              data-testid="reject-link"
            >
              Reject
            </Button>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};

export default InvitePage;
